import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { Box, ButtonBase, Collapse, Stack, Typography } from '@mui/material';
import React from 'react';
import { CharacterGrade, SoundGrade, SummaryExercise, speakingScore } from '../../../student/mockLessonContent';
import { BLUE, cardSx, ERROR_TEXT, GREEN, INTER, NAVY, POPPINS, RED, SLATE, SURFACE_SOFT, YELLOW } from '../../../student/studentStyles';
import AudioPlayButton from '../../../student/components/AudioPlayButton';
import ProgressRing from '../../../student/components/ProgressRing';
import ExerciseLabel from '../../../student/components/exercises/ExerciseLabel';
import { useExerciseLayout } from './exerciseLayout';

const gradeColors: Record<SoundGrade, string> = { good: GREEN, ok: YELLOW, bad: RED };

// One attempt: the expected reading on top, what was heard underneath, and the
// initial / final / tone bars in between — the same language as the per-exercise
// pronunciation detail.
function AttemptStrip({ grades }: { grades: CharacterGrade[] }) {
    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {grades.map((grade, index) => (
                <Box key={`${grade.character}-${index}`} sx={{ minWidth: 46, p: 0.75, borderRadius: '10px', backgroundColor: SURFACE_SOFT, textAlign: 'center' }}>
                    <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE }}>
                        {grade.pinyinExpected}
                    </Typography>
                    <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 17, color: NAVY, lineHeight: 1.2 }}>
                        {grade.character}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '2px', mt: 0.5 }}>
                        {([grade.initial, grade.final, grade.tone] as const).map((value, part) => (
                            <Box key={part} sx={{ flex: 1, height: 4, borderRadius: '999px', backgroundColor: gradeColors[value] }} />
                        ))}
                    </Box>
                    <Typography sx={{ fontFamily: INTER, fontSize: 11, color: grade.pinyinSpoken === grade.pinyinExpected ? SLATE : ERROR_TEXT, mt: 0.5 }}>
                        {grade.pinyinSpoken}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

// "Speaking Summary" — every sentence the student spoke this lesson, best
// attempt first, earlier tries collapsed underneath.
export default function SummaryExerciseCard({ exercise }: { exercise: SummaryExercise }) {
    const desktop = useExerciseLayout() === 'desktop';
    const [expanded, setExpanded] = React.useState<number[]>([]);

    const toggle = (index: number) => {
        setExpanded(prev => (prev.includes(index) ? prev.filter(item => item !== index) : [...prev, index]));
    };

    return (
        <Box>
            <ExerciseLabel>SPEAKING SUMMARY</ExerciseLabel>
            <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 20, color: NAVY, lineHeight: 1.35 }}>
                {exercise.title}
            </Typography>
            <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: desktop ? 'repeat(2, minmax(0, 1fr))' : '1fr', gap: 1.5, alignItems: 'start' }}>
                {exercise.sentences.map((sentence, index) => {
                    const [latest, ...earlier] = sentence.attempts;
                    const isExpanded = expanded.includes(index);
                    return (
                        <Box key={sentence.english} sx={{ ...cardSx, p: 2 }}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                <ProgressRing percent={speakingScore(latest)} size={40} thickness={4} />
                                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: SLATE, flex: 1, minWidth: 0 }}>
                                    {sentence.english}
                                </Typography>
                                <AudioPlayButton ariaLabel={`Play your recording of ${sentence.english}`} size={36} />
                            </Stack>
                            <Box sx={{ mt: 1.5 }}>
                                <AttemptStrip grades={latest} />
                            </Box>
                            {earlier.length > 0 && (
                                <>
                                    <ButtonBase
                                        onClick={() => toggle(index)}
                                        sx={{ mt: 1, borderRadius: '999px', py: 0.5, fontFamily: INTER, fontWeight: 700, fontSize: 12, color: BLUE, gap: 0.5 }}
                                    >
                                        {earlier.length === 1 ? '1 earlier attempt' : `${earlier.length} earlier attempts`}
                                        <ExpandMoreRoundedIcon sx={{ fontSize: 18, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 200ms ease' }} />
                                    </ButtonBase>
                                    <Collapse in={isExpanded}>
                                        <Stack spacing={1} sx={{ mt: 1, opacity: 0.75 }}>
                                            {earlier.map((attempt, attemptIndex) => (
                                                <AttemptStrip key={attemptIndex} grades={attempt} />
                                            ))}
                                        </Stack>
                                    </Collapse>
                                </>
                            )}
                        </Box>
                    );
                })}
            </Box>
            <Typography sx={{ fontFamily: INTER, fontSize: 11, color: SLATE, mt: 1.5, textAlign: 'center' }}>
                Top row: what to say · bars: initial · final · tone · bottom row: what we heard
            </Typography>
        </Box>
    );
}
